import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, NOMBRES, conditions, seededRandom } from "../spec";

const SHEET = "MD Ranking";
const TABLE = "Desempeno";
const REGIONES = ["Norte", "Sur", "Centro", "Occidente"] as const;

const rnd = seededRandom(2718);
// Distinct values everywhere we sort, so there are no ties to break.
const clientesNuevos = Array.from({ length: 20 }, (_, i) => i + 3);
for (let i = clientesNuevos.length - 1; i > 0; i--) {
  const j = rnd.int(0, i);
  [clientesNuevos[i], clientesNuevos[j]] = [clientesNuevos[j], clientesNuevos[i]];
}
// Vendedor, Región, Ventas, Meta, Clientes nuevos, Antigüedad
const ROWS = NOMBRES.slice(0, 20).map((nombre, i) => [
  nombre,
  REGIONES[i % REGIONES.length],
  400000 + i * 7350 + rnd.int(0, 120) * 1000,
  rnd.round(450000, 600000, 10000),
  clientesNuevos[i],
  rnd.int(1, 12),
] as [string, string, number, number, number, number]);

type Row = (typeof ROWS)[number];
const IN = { region: "Centro" };
const byText = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" });
const sorted = (cmp: (a: Row, b: Row) => number) => [...ROWS].sort(cmp);
const byVentasDesc = (a: Row, b: Row) => b[2] - a[2];
const quinto = [...ROWS].map((r) => r[2]).sort((a, b) => b - a)[4];

const EXPECTED: CellValue[][][] = [
  sorted(byVentasDesc),
  [...ROWS].map((r) => r[0]).sort(byText).map((v) => [v]),
  sorted((a, b) => byText(a[1], b[1]) || b[2] - a[2]),
  sorted((a, b) => b[4] - a[4]).map((r) => [r[0]]),
  sorted((a, b) => b[2] / b[3] - a[2] / a[3]),
  sorted(byVentasDesc).filter((r) => r[2] >= quinto),
  sorted(byVentasDesc).filter((r) => r[1] === IN.region).map((r) => [r[0], r[1], r[2]]),
];

const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`J${row}`);
  return row + result.length + 4;
}, 8);

const LABELS = [
  "Tarea 1 · Ranking por ventas",
  "Tarea 2 · Vendedores A–Z",
  "Tarea 3 · Por región y ventas",
  "Tarea 4 · Por clientes nuevos",
  "Tarea 5 · Por % de cumplimiento",
  "Tarea 6 · Top 5",
  "Tarea 7 · Ranking de la región",
];
const FNS = [["SORT"], ["SORT"], ["SORTBY"], ["SORTBY"], ["SORTBY"], ["SORT", "FILTER", "LARGE"], ["SORT", "FILTER"]];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-ordenar",
  sheets: [
    {
      name: SHEET,
      title: "Ranking de vendedores",
      subtitle: "Resultados del semestre — cada ranking se derrama desde su celda amarilla",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Vendedor", "Región", "Ventas", "Meta", "Clientes nuevos", "Antigüedad"],
          rows: ROWS,
          formats: { Ventas: "$#,##0", Meta: "$#,##0" },
        },
      ],
      inputs: [{ address: "J4", label: "Región", value: IN.region }],
      answers: ANCHORS.map((address, i) => ({ address, label: LABELS[i], labelPosition: "above" as const })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-ordenar",
  title: "ORDENAR y ORDENARPOR: Ranking de vendedores",
  subtitle: "Rankings que se reordenan solos",
  description:
    "La dirección comercial quiere ver a sus 20 vendedores ordenados de muchas formas: por ventas, por región, por clientes nuevos, por % de cumplimiento… y un Top 5.\n\nORDENAR ordena por el número de columna que le indiques. ORDENARPOR ordena por cualquier rango, incluso uno calculado o que no aparece en el resultado, y permite varios niveles.",
  topics: [
    "ORDENAR por número de columna y sentido (1 / -1)",
    "Varios niveles con ORDENARPOR",
    "Ordenar por una columna que no se muestra",
    "Ordenar por un cálculo",
    "Top N con FILTRAR y K.ESIMO.MAYOR",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Ranking por ventas",
      description: `En la hoja <strong>MD Ranking</strong>, celda <strong>${ANCHORS[0]}</strong>: muestra la tabla <strong>Desempeno</strong> completa ordenada por <strong>Ventas</strong> de <strong>mayor a menor</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[0])}.`,
        `ORDENAR(matriz, índice_de_orden, criterio): Ventas es la <strong>3.ª</strong> columna de la tabla y ${F("-1")} significa descendente.`,
        `Escribe ${F("=ORDENAR(Desempeno,3,-1)")}.`,
      ],
      expectedResult: "La tabla del vendedor que más vendió al que menos",
      finalFormula: "=ORDENAR(Desempeno,3,-1)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Vendedores en orden alfabético",
      description: `En <strong>${ANCHORS[1]}</strong>: muestra solo la columna <strong>Vendedor</strong> ordenada de la A a la Z.`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        "Si solo das una columna y no indicas nada más, ORDENAR la acomoda de forma ascendente.",
        `Escribe ${F("=ORDENAR(Desempeno[Vendedor])")}.`,
      ],
      expectedResult: "20 nombres en orden alfabético",
      finalFormula: "=ORDENAR(Desempeno[Vendedor])",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Por región y luego por ventas",
      description: conditions(
        `En <strong>${ANCHORS[2]}</strong>: muestra la tabla completa ordenada en <strong>dos niveles</strong>:`,
        [
          "Primero por <strong>Región</strong>, de la A a la Z",
          "Dentro de cada región, por <strong>Ventas</strong> de mayor a menor",
        ]
      ),
      solution: [
        `Selecciona ${F(ANCHORS[2])}.`,
        "ORDENARPOR recibe pares rango–sentido: el primer par es el primer nivel, el segundo par desempata.",
        `Escribe ${F("=ORDENARPOR(Desempeno,Desempeno[Región],1,Desempeno[Ventas],-1)")}.`,
      ],
      expectedResult: "Centro, Norte, Occidente y Sur, cada una con su mejor vendedor arriba",
      finalFormula: "=ORDENARPOR(Desempeno,Desempeno[Región],1,Desempeno[Ventas],-1)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Ordenar por una columna que no se muestra",
      description: `En <strong>${ANCHORS[3]}</strong>: muestra solo los nombres de los vendedores, ordenados por <strong>Clientes nuevos</strong> de mayor a menor. El resultado debe tener <strong>una sola columna</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[3])}.`,
        "Con ORDENAR no se puede: solo ordena por columnas que forman parte del resultado. ORDENARPOR sí.",
        `Escribe ${F("=ORDENARPOR(Desempeno[Vendedor],Desempeno[Clientes nuevos],-1)")}.`,
      ],
      expectedResult: "Nombres del que más clientes nuevos consiguió al que menos",
      finalFormula: "=ORDENARPOR(Desempeno[Vendedor],Desempeno[Clientes nuevos],-1)",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Ordenar por % de cumplimiento",
      description: `En <strong>${ANCHORS[4]}</strong>: muestra la tabla completa ordenada por el <strong>% de cumplimiento</strong> (Ventas ÷ Meta), de mayor a menor. Esa columna <strong>no existe</strong>: calcúlala dentro de la fórmula.`,
      solution: [
        `Selecciona ${F(ANCHORS[4])}.`,
        `ORDENARPOR acepta un cálculo como rango de orden: ${F("Desempeno[Ventas]/Desempeno[Meta]")}.`,
        `Escribe ${F("=ORDENARPOR(Desempeno,Desempeno[Ventas]/Desempeno[Meta],-1)")}.`,
      ],
      expectedResult: "Primero quien más superó su meta",
      finalFormula: "=ORDENARPOR(Desempeno,Desempeno[Ventas]/Desempeno[Meta],-1)",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Top 5",
      description: conditions(`En <strong>${ANCHORS[5]}</strong>: muestra solo los <strong>5 vendedores con más ventas</strong>, ordenados de mayor a menor.`, [
        "<strong>K.ESIMO.MAYOR</strong>(Ventas, 5) da la 5.ª venta más alta: es el corte",
        "<strong>FILTRAR</strong> deja las filas con ventas mayores o iguales a ese corte",
        "<strong>ORDENAR</strong> acomoda el resultado",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        `Escribe ${F("=ORDENAR(FILTRAR(Desempeno,Desempeno[Ventas]>=K.ESIMO.MAYOR(Desempeno[Ventas],5)),3,-1)")}.`,
        "Cambia el 5 por 3 para ver un Top 3.",
      ],
      expectedResult: "Las 5 filas con mayores ventas",
      finalFormula: "=ORDENAR(FILTRAR(Desempeno,Desempeno[Ventas]>=K.ESIMO.MAYOR(Desempeno[Ventas],5)),3,-1)",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Ranking de una región",
      description: conditions(`En <strong>${ANCHORS[6]}</strong>: arma el ranking de la región de <strong>J4</strong>:`, [
        "Solo las columnas <strong>Vendedor</strong>, <strong>Región</strong> y <strong>Ventas</strong>",
        "Solo los vendedores de esa región",
        "Ordenados por Ventas de <strong>mayor a menor</strong>",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[6])}.`,
        `Filtra solo las 3 columnas: ${F("Desempeno[[Vendedor]:[Ventas]]")}. En ese resultado, Ventas es la columna 3.`,
        `Escribe ${F("=ORDENAR(FILTRAR(Desempeno[[Vendedor]:[Ventas]],Desempeno[Región]=J4),3,-1)")}.`,
      ],
      expectedResult: "Vendedores del Centro, del mejor al peor",
      finalFormula: "=ORDENAR(FILTRAR(Desempeno[[Vendedor]:[Ventas]],Desempeno[Región]=J4),3,-1)",
    },
  ],
};
